// Deterministic anomaly detection — no AI / API required. Fully explainable.

export const DEFAULT_THRESHOLDS = { observe: 25, warning: 50, critical: 75 };

// Compute an explainable anomaly score from measurable factors.
// integrityBroken forces INTEGRITY FAILURE regardless of score.
export function scoreAnomaly(
  { baselineDifference = 0, temporalDelta = 0, driftMagnitude = 0, integrityBroken = false },
  thresholds = DEFAULT_THRESHOLDS
) {
  const factors = [
    {
      label: "Baseline difference",
      value: baselineDifference,
      contribution: Math.min(35, Math.abs(baselineDifference) * 15),
      note: "Distance between current value and captured baseline.",
    },
    {
      label: "Temporal deviation",
      value: temporalDelta,
      contribution: Math.min(25, (Math.abs(temporalDelta) / 200) * 25),
      note: "Deviation in elapsed time between observations (ms).",
    },
    {
      label: "Drift magnitude",
      value: driftMagnitude,
      contribution: Math.min(40, Math.abs(driftMagnitude) * 18),
      note: "Absolute measured drift for this observation.",
    },
    {
      label: "Integrity state",
      value: integrityBroken ? 1 : 0,
      contribution: integrityBroken ? 100 : 0,
      note: integrityBroken
        ? "Cryptographic integrity check FAILED — overrides score."
        : "Cryptographic integrity intact.",
    },
  ];

  const raw = factors.reduce((s, f) => s + f.contribution, 0);
  const score = Math.min(100, Number(raw.toFixed(1)));

  let classification;
  if (integrityBroken) classification = "INTEGRITY FAILURE";
  else if (score >= thresholds.critical) classification = "CRITICAL";
  else if (score >= thresholds.warning) classification = "WARNING";
  else if (score >= thresholds.observe) classification = "OBSERVE";
  else classification = "NORMAL";

  const explanation = integrityBroken
    ? "Classified INTEGRITY FAILURE because the record hash does not match its content (tamper detected)."
    : `Score ${score}/100 = ` +
      factors
        .filter((f) => f.contribution > 0)
        .map((f) => `${f.label} +${f.contribution.toFixed(1)}`)
        .join(", ") +
      `. Thresholds: OBSERVE ≥ ${thresholds.observe}, WARNING ≥ ${thresholds.warning}, CRITICAL ≥ ${thresholds.critical}.`;

  return { score, classification, factors, explanation };
}

export const CLASS_STYLES = {
  NORMAL: { text: "text-emerald-300", bg: "bg-emerald-950", border: "border-emerald-600", icon: "CheckCircle2" },
  OBSERVE: { text: "text-sky-300", bg: "bg-sky-950", border: "border-sky-600", icon: "Eye" },
  WARNING: { text: "text-amber-300", bg: "bg-amber-950", border: "border-amber-600", icon: "AlertTriangle" },
  CRITICAL: { text: "text-rose-300", bg: "bg-rose-950", border: "border-rose-600", icon: "AlertOctagon" },
  "INTEGRITY FAILURE": { text: "text-rose-200", bg: "bg-rose-900", border: "border-rose-500", icon: "ShieldAlert" },
};
